import React, { useState, useEffect } from "react";
import { adminCreateVariant, adminUpdateVariant } from "../api/admin";
import { toast } from "react-toastify";

const VariantForm = ({ productId, productUrl, productName, variant, onSuccess, onCancel }) => {
  const [variantType, setVariantType] = useState("");
  // ... (rest of state stays same)
  const [variantValues, setVariantValues] = useState("");
  const [price, setPrice] = useState("");
  const [offerPrice, setOfferPrice] = useState("");
  const [offerPercentage, setOfferPercentage] = useState("");
  const [sku, setSku] = useState("");
  const [stock, setStock] = useState("");
  const [variantUrl, setVariantUrl] = useState("");
  const [totalSold, setTotalSold] = useState(0);
  const [schemaMarkup, setSchemaMarkup] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  
  // Attributes state
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [hex, setHex] = useState("");

  const resetForm = () => {
    setVariantType("");
    setVariantValues("");
    setPrice("");
    setOfferPrice("");
    setOfferPercentage("");
    setSku("");
    setStock("");
    setVariantUrl("");
    setTotalSold(0);
    setSchemaMarkup("");
    setIsDefault(false);
    setColor("");
    setSize("");
    setHex("");
  };

  useEffect(() => {
    if (variant) {
      setVariantType(variant.variant_Type || variant.variant_type || variant.variantType || "");
      setVariantValues(variant.variant_Values || variant.variant_Value || variant.variantValue || "");
      setPrice(variant.price ?? "");
      setOfferPrice(variant.offer_price ?? "");
      setOfferPercentage(variant.offer_percentage ?? "");
      setSku(variant.sku ?? "");
      setStock(variant.stock ?? "");
      setTotalSold(variant.total_sold ?? 0);
      setSchemaMarkup(variant.schema_markup ?? "");
      setIsDefault(variant.isDefault || false);
      
      const attrs = variant.attributes || {};
      setColor(attrs.color || "");
      setSize(attrs.size || "");
      setHex(attrs.hex || "");
      setVariantUrl(variant.variant_url || "");
    } else {
      resetForm();
    }
  }, [variant]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Create pure JSON payload for the backend
    const payload = {
      variant_Type: variantType,
      variant_Values: variantValues,
      price: Number(price),
      offer_price: Number(offerPrice) || 0,
      offer_percentage: Number(price) > Number(offerPrice) && Number(offerPrice) > 0 
        ? Math.round(((Number(price) - Number(offerPrice)) / Number(price)) * 100) 
        : 0,
      sku: sku,
      stock: Number(stock),
      variant_url: variantUrl,
      total_sold: Number(totalSold) || 0,
      schema_markup: schemaMarkup,
      isDefault: isDefault,
      status: "active",
      attributes: { color, size, hex }
    };

    try {
      if (variant?.id || variant?._id) {
        await adminUpdateVariant(variant._id || variant.id, payload);
      } else {
        await adminCreateVariant(productId, payload);
      }
      resetForm();
      onSuccess();
      toast.success("Variant saved successfully! 🎉");
    } catch (err) {
      console.error("Error saving variant:", err);
      toast.error(err.response?.data?.message || "Failed to save variant. ❌");
    }
  };

  const handleAutoResize = (e) => {
    const target = e.target || e;
    target.style.height = "auto";
    target.style.height = `${target.scrollHeight}px`;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const textareas = document.querySelectorAll('.admin-form textarea');
      textareas.forEach(ta => handleAutoResize(ta));
    }, 100);
    return () => clearTimeout(timer);
  }, [variant]);

  return (
    <form onSubmit={handleSubmit} className="admin-form">
      <div className="form-body">
        {/* Section 1: Variant Details */}
        <div className="form-section-title">📦 Variant Details</div>
        <div className="form-grid">
          <div className="form-group">
            <label>Color</label>
            <input
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="e.g., Brown"
            />
          </div>
          <div className="form-group">
            <label>Size</label>
            <input
              value={size}
              onChange={(e) => setSize(e.target.value)}
              placeholder="e.g., 44"
            />
          </div>
          <div className="form-group">
            <label>Hex Code</label>
            <input
              value={hex}
              onChange={(e) => setHex(e.target.value)}
              placeholder="e.g., #11111"
            />
          </div>
          
          <div className="form-group">
            <label>Price</label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Retail Price"
            />
          </div>
          <div className="form-group">
            <label>Offer Price <span className="required-star">*</span></label>
            <input
              type="number"
              value={offerPrice}
              onChange={(e) => setOfferPrice(e.target.value)}
              placeholder="Sale Price"
              required
            />
          </div>
          <div className="form-group">
            <label>Offer % (Auto)</label>
            <input
              type="number"
              value={(() => {
                const p = Number(price) || 0;
                const o = Number(offerPrice) || 0;
                if (p > o && o > 0) return Math.round(((p - o) / p) * 100);
                return 0;
              })()}
              readOnly
              className="readonly-input"
              style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>
          <div className="form-group">
            <label>SKU</label>
            <input
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="Stock Keeping Unit"
            />
          </div>
          <div className="form-group">
            <label>Stock <span className="required-star">*</span></label>
            <input
              type="number"
              value={stock}
              onChange={(e) => setStock(e.target.value)}
              placeholder="Available Quantity"
              required
            />
          </div>
          <div className="form-group">
            <label>Variant URL</label>
            <input
              value={variantUrl}
              onChange={(e) => setVariantUrl(e.target.value)}
              placeholder="slug-for-variant"
            />
          </div>
          <div className="form-group">
            <label>Total Sold</label>
            <input
              type="number"
              value={totalSold}
              onChange={(e) => setTotalSold(e.target.value)}
            />
          </div>
        </div>

        {/* Section 3: Metadata & Configuration */}
        <div className="form-section-title">⚙️ Metadata & Config</div>
        <div className="form-group full-width">
          <label>Schema Markup</label>
          <textarea
            value={schemaMarkup}
            onChange={(e) => {
              setSchemaMarkup(e.target.value);
              handleAutoResize(e);
            }}
            onInput={handleAutoResize}
            placeholder="Enter Schema Markup JSON"
            style={{ minHeight: '80px', overflowY: 'hidden' }}
          />
        </div>

        <div className="offer-options">
          <label className="checkbox-card">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            <span>Default Variant</span>
          </label>
        </div>



        <div className="form-footer">
          <button type="submit" className="btn-submit">
            {variant ? "Update Variant" : "Save Variant"}
          </button>
          <button type="button" className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

export default VariantForm;
